/**
 * 系统配置 · 中台同步忽略目录（「模型管理 → 模型同步」用）。
 *
 * 中台安装目录按已登记的模型类别（model_name，目录唯一）直接找名称，没登记的目录同步时以自动名登记；
 * 这里只维护同步时要跳过的目录（如中台测试脏数据），每条必须写原因。
 * 配置键沿用 sysconfig:model_sync:ndp_slots，值形如 {"ignored": {目录: {reason}}}。
 */
import { useState } from 'react';
import { App, Button, Empty, Input, Popconfirm, Segmented, Space, Tooltip, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, RollbackOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';
import JsonEditor from '@components/JsonEditor';
import { getNdpModelSlots, resetNdpModelSlots, updateNdpModelSlots } from '@apis/modelSync';
import { errorText, ignoredError, ignoredToJson, parseIgnored, type IgnoredRow } from '@/utils/modelSync';
import { Panel, PanelError, PanelLoading, RefreshButton, SOURCE_PILL, useConfigResource, useReportDirty } from './shared';

const { Text } = Typography;
const CARD_ID = 'sec-ndp-model-slots';

function jsonError(raw: string): string | null {
  try {
    return ignoredError(parseIgnored(raw));
  } catch (e) {
    return `不是合法 JSON：${(e as Error).message}`;
  }
}

export default function NdpModelSlotsCard({ index }: { index: number }) {
  const { message } = App.useApp();
  const [view, setView] = useState<'form' | 'json'>('form');
  const [rows, setRows] = useState<IgnoredRow[]>([]);
  const [jsonDraft, setJsonDraft] = useState('{}');
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const loadDraft = (raw: string) => {
    setJsonDraft(raw);
    setSaveError(null);
    if (jsonError(raw)) {
      setView('json');
      setRows([]);
    } else {
      setRows(parseIgnored(raw));
    }
  };
  const { loading, config, applyConfig, fetchConfig } = useConfigResource({
    id: CARD_ID, title: '中台同步忽略目录', fetcher: getNdpModelSlots, onLoaded: (cfg) => loadDraft(cfg.currentValue),
  });

  const draft = view === 'form' ? ignoredToJson(rows) : jsonDraft;
  const error = jsonError(draft);
  const dirty = config !== null && (() => {
    try { return JSON.stringify(JSON.parse(draft)) !== JSON.stringify(JSON.parse(config.currentValue)); }
    catch { return true; }
  })();
  useReportDirty(CARD_ID, dirty);

  const changeView = (next: 'form' | 'json') => {
    if (next === view) return;
    if (error) { message.warning('请先修正当前编辑内容中的错误'); return; }
    if (next === 'json') setJsonDraft(ignoredToJson(rows));
    else setRows(parseIgnored(jsonDraft));
    setView(next);
  };

  const update = (i: number, changes: Partial<IgnoredRow>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...changes } : r)));

  const handleSave = async () => {
    if (error || saving || resetting) return;
    setSaving(true);
    setSaveError(null);
    try {
      applyConfig(await updateNdpModelSlots(draft));
      message.success('已保存，下次预览 / 同步立即生效');
    } catch (e) {
      setSaveError(errorText(e, '保存失败，请检查后重试'));
    } finally {
      setSaving(false);
    }
  };
  const handleReset = async () => {
    setResetting(true);
    try {
      applyConfig(await resetNdpModelSlots());
      message.success('已恢复默认（忽略列表清空）');
    } catch (e) {
      // 请求层已弹出错误提示
      console.error('恢复默认忽略目录失败', e);
    } finally {
      setResetting(false);
    }
  };

  const busy = saving || resetting || loading;

  return (
    <Panel
      id={CARD_ID} index={index} variant="cfg" kicker="MODEL SYNC" title="中台同步忽略目录" dirty={dirty}
      subtitle="同步中台发版时跳过这些安装目录下的模型（如测试脏数据），发版其它目录照常同步；每条必须写原因。"
      info={(
        <div>
          中台安装目录按已登记的模型类别直接对应；没登记过的目录会在同步时以自动名登记（预览里会提示）。
          只有确实不该进平台的目录才放到这里。
        </div>
      )}
      tools={(
        <>
          <Segmented size="small" value={view} options={[{ value: 'form', label: '表单' }, { value: 'json', label: 'JSON' }]}
            onChange={(v) => changeView(v as 'form' | 'json')} disabled={busy || !config} />
          <RefreshButton loading={loading} onClick={fetchConfig} />
        </>
      )}
    >
      {loading && !config ? <PanelLoading /> : !config ? <PanelError onRetry={fetchConfig} /> : (
        <>
          <div className="sc-meta">{SOURCE_PILL[config.source]}<span className="sc-editor-hint">保存后对下一次预览 / 同步立即生效。</span></div>

          {view === 'json' ? (
            <JsonEditor value={jsonDraft} onChange={setJsonDraft} validate={(_, raw) => jsonError(raw)}
              onSave={handleSave} minRows={8} maxRows={20} readOnly={saving || resetting} />
          ) : (
            <div>
              {rows.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="没有忽略的目录" />}
              {rows.map((r, i) => (
                <Space key={i} wrap style={{ display: 'flex', marginBottom: 8 }}>
                  <Input style={{ width: 420, maxWidth: '100%' }} value={r.installDir} disabled={busy}
                    placeholder="安装目录（相对 /home/caros/cyberrt/data/；脏数据可直接填原始 dirInfo）"
                    onChange={(e) => update(i, { installDir: e.target.value })} />
                  <Input style={{ width: 300, maxWidth: '100%' }} value={r.reason} placeholder="原因（必填）" maxLength={200} disabled={busy}
                    onChange={(e) => update(i, { reason: e.target.value })} />
                  {r.by && <Text type="secondary" style={{ fontSize: 12 }}>{r.by}</Text>}
                  <Tooltip title="移除：之后同步不再跳过该目录">
                    <Button type="text" danger icon={<DeleteOutlined />} aria-label="移除忽略目录" disabled={busy}
                      onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} />
                  </Tooltip>
                </Space>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} disabled={busy}
                onClick={() => setRows((rs) => [...rs, { installDir: '', reason: '' }])}>新增忽略目录</Button>
            </div>
          )}

          <div className="sc-editor">
            <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} disabled={!dirty || !!error || resetting || loading} onClick={() => void handleSave()}>保存</Button>
            <Button size="small" icon={<UndoOutlined />} title="撤销本地改动" aria-label="撤销忽略目录改动" disabled={!dirty || saving || resetting}
              onClick={() => loadDraft(config.currentValue)} />
            <Popconfirm title="恢复默认？" description="忽略列表清空。" okText="恢复默认" cancelText="取消" onConfirm={handleReset}>
              <Button size="small" danger icon={<RollbackOutlined />} loading={resetting} disabled={saving || loading}>恢复默认</Button>
            </Popconfirm>
            {(error || saveError) && <span className="sc-editor-hint bad" role="alert">{error ?? saveError}</span>}
          </div>
        </>
      )}
    </Panel>
  );
}
